"use client"

import { useMutation, useQueryClient } from "@tanstack/react-query"
import { useRouter } from "next/navigation"
import React, { useEffect, useState } from "react"
import { useForm } from "react-hook-form"
import {
  getOtpErrorMessage,
  sendTriiboOtp,
  validateTriiboOtp,
} from "@/api/auth"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from "@/components/ui/input-otp"
import { resolvePostLoginRedirect } from "@/utilities/auth-callback"

const OTP_LENGTH = 6
const OTP_RESEND_SECONDS = 25
const AUTH_ME_QUERY_KEY = ["auth", "me"] as const
const CART_ITEMS_QUERY_KEY = ["cart", "items"] as const
const AUTH_SYNC_ATTEMPTS = 5
const AUTH_SYNC_WAIT_MS = 150

interface AuthMeResponse {
  user: {
    name?: string
  } | null
}

interface OtpFormValues {
  code: string
}

interface OtpStepDialogProps {
  isOpen: boolean
  cellphone: string
  cellphoneDisplay: string
  transactionId: string | null
  callbackParam: string | null
  resendSeed: number
  onDismiss: () => void
  onOtpResent: (transactionId: string) => void
  onRegistrationRequired: (triiboId: string) => void
  onAuthenticated: (payload: {
    apiRedirect?: string | null
    shouldSyncCart: boolean
  }) => Promise<void>
}

const wait = async (milliseconds: number): Promise<void> => {
  await new Promise((resolve) => {
    setTimeout(resolve, milliseconds)
  })
}

const isLoggedInValue = (value: unknown): boolean => {
  return value === true || value === "true" || value === 1 || value === "1"
}

const isLoggedOutValue = (value: unknown): boolean => {
  return value === false || value === "false" || value === 0 || value === "0"
}

export const OtpStepDialog = ({
  isOpen,
  cellphone,
  cellphoneDisplay,
  transactionId,
  callbackParam,
  resendSeed,
  onDismiss,
  onOtpResent,
  onRegistrationRequired,
  onAuthenticated,
}: OtpStepDialogProps) => {
  const router = useRouter()
  const queryClient = useQueryClient()
  const { watch, setValue, reset } = useForm<OtpFormValues>({
    defaultValues: { code: "" },
  })

  const verificationCode = watch("code")

  const [secondsToResend, setSecondsToResend] =
    React.useState(OTP_RESEND_SECONDS)
  const [showResentMessage, setShowResentMessage] = React.useState(false)
  const [validateError, setValidateError] = useState<string | null>(null)
  const [resendError, setResendError] = useState<string | null>(null)

  const canResendCode = secondsToResend === 0
  const isEnterDisabled = verificationCode.length !== OTP_LENGTH

  const resendOtpMutation = useMutation({
    mutationFn: async () => {
      return await sendTriiboOtp({
        cellphone,
        type: "whatsapp",
      })
    },
  })

  const validateOtpMutation = useMutation({
    mutationFn: async (code: string) => {
      if (!transactionId) {
        throw new Error("Nao foi possivel identificar a transacao de OTP.")
      }

      return await validateTriiboOtp({
        code,
        cellphone,
        transactionId,
      })
    },
  })

  const syncPostLoginQueries = async (): Promise<void> => {
    await queryClient.invalidateQueries({
      queryKey: AUTH_ME_QUERY_KEY,
    })

    for (let attempt = 0; attempt < AUTH_SYNC_ATTEMPTS; attempt += 1) {
      const authMeData =
        queryClient.getQueryData<AuthMeResponse>(AUTH_ME_QUERY_KEY)

      if (authMeData?.user) {
        break
      }

      await queryClient.refetchQueries({
        queryKey: AUTH_ME_QUERY_KEY,
      })

      await wait(AUTH_SYNC_WAIT_MS)
    }

    await queryClient.invalidateQueries({
      queryKey: CART_ITEMS_QUERY_KEY,
    })

    await queryClient.refetchQueries({
      queryKey: CART_ITEMS_QUERY_KEY,
    })
  }

  const handleResendClick = async () => {
    setResendError(null) // Clear previous errors
    const response = await resendOtpMutation.mutateAsync().catch(() => null)

    if (!response) {
      setResendError("Falha ao reenviar codigo via WhatsApp.")
      return
    }

    if (!response.success) {
      setResendError(
        response.message || "Nao foi possivel reenviar o codigo via WhatsApp."
      )
      return
    }

    const newTransactionId = response.success?.transactionId

    if (!newTransactionId) {
      setResendError("Nao foi possivel obter o ID da transacao.")
      return
    }

    onOtpResent(newTransactionId)
    setSecondsToResend(OTP_RESEND_SECONDS)
    setShowResentMessage(true)
  }

  const handleValidateOtp = async () => {
    if (!transactionId) {
      return
    }

    setValidateError(null) // Clear previous errors
    const response = await validateOtpMutation
      .mutateAsync(verificationCode)
      .catch(() => null)

    if (!response) {
      setValidateError("Falha ao validar o codigo.")
      return
    }

    if (!response.success) {
      const errorMessage =
        response.error?.message ||
        getOtpErrorMessage(response.error?.errorCode) ||
        "Nao foi possivel validar o codigo."
      setValidateError(errorMessage)
      return
    }

    const loggedIn = isLoggedInValue(response.data?.logged_in)
    const loggedOut = isLoggedOutValue(response.data?.logged_in)
    const requiresRegistration = loggedOut && Boolean(response.data?.triiboId)

    if (requiresRegistration && response.data?.triiboId) {
      onRegistrationRequired(response.data.triiboId)
      return
    }

    const shouldSyncCart = loggedIn || !loggedOut

    if (shouldSyncCart) {
      await syncPostLoginQueries()
    }

    await onAuthenticated({
      apiRedirect: response.redirect,
      shouldSyncCart,
    })

    const redirectUrl = resolvePostLoginRedirect({
      callback: callbackParam,
      apiRedirect: response.redirect,
      fallback: "/minha-conta",
    })

    router.push(redirectUrl)
  }

  useEffect(() => {
    if (!isOpen) {
      return
    }

    reset({ code: "" })
    setSecondsToResend(OTP_RESEND_SECONDS)
    setShowResentMessage(false)
    setValidateError(null)
    setResendError(null)
  }, [isOpen, reset])

  useEffect(() => {
    if (!isOpen || resendSeed < 0) {
      return
    }

    reset({ code: "" })
    setSecondsToResend(OTP_RESEND_SECONDS)
    setValidateError(null)
    setResendError(null)
  }, [isOpen, resendSeed, reset])

  useEffect(() => {
    if (!isOpen || secondsToResend === 0) {
      return
    }

    const intervalId = setInterval(() => {
      setSecondsToResend((previousSeconds) => {
        if (previousSeconds <= 1) {
          clearInterval(intervalId)
          return 0
        }

        return previousSeconds - 1
      })
    }, 1000)

    return () => {
      clearInterval(intervalId)
    }
  }, [isOpen, secondsToResend])

  return (
    <Dialog onOpenChange={(open) => !open && onDismiss()} open={isOpen}>
      <DialogContent className='max-w-md'>
        <DialogHeader className='space-y-2 text-left'>
          <DialogTitle>Entre com o codigo</DialogTitle>
          <DialogDescription>
            Enviamos um codigo para {cellphoneDisplay}. Informe-o abaixo para
            continuar.
          </DialogDescription>
        </DialogHeader>

        <div className='space-y-4'>
          <InputOTP
            aria-label='Codigo de verificacao'
            maxLength={OTP_LENGTH}
            onChange={(value) => setValue("code", value)}
            value={verificationCode}
          >
            <InputOTPGroup className='mx-auto'>
              <InputOTPSlot index={0} />
              <InputOTPSlot index={1} />
              <InputOTPSlot index={2} />
              <InputOTPSlot index={3} />
              <InputOTPSlot index={4} />
              <InputOTPSlot index={5} />
            </InputOTPGroup>
          </InputOTP>

          {validateError ? (
            <p className='text-red-600 text-sm'>{validateError}</p>
          ) : null}

          {resendError ? (
            <p className='text-red-600 text-sm'>{resendError}</p>
          ) : null}

          <div className='text-right text-muted-foreground text-sm'>
            {canResendCode ? (
              <button
                className='font-medium text-primary hover:underline'
                disabled={resendOtpMutation.isPending}
                onClick={handleResendClick}
                type='button'
              >
                {resendOtpMutation.isPending
                  ? "Reenviando..."
                  : "Reenviar codigo"}
              </button>
            ) : (
              <span>Reenviar codigo em {secondsToResend}s</span>
            )}
          </div>

          {showResentMessage ? (
            <p className='text-right text-muted-foreground text-xs'>
              Codigo reenviado.
            </p>
          ) : null}

          <Button
            className='w-full'
            disabled={isEnterDisabled || validateOtpMutation.isPending}
            onClick={handleValidateOtp}
            type='button'
          >
            {validateOtpMutation.isPending ? "Validando..." : "Entrar"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
